import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, User, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { getFriendlyApiMessage, requestApiJson } from "@/lib/api-client";

export const InspectorManager = ({ isOpen, onClose, onSelectInspector, currentInspector }) => {
  const [inspectors, setInspectors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newInspector, setNewInspector] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    if (isOpen) {
      loadInspectors();
    }
  }, [isOpen]);

  const loadInspectors = async () => {
    setIsLoading(true);
    try {
      const data = await requestApiJson("/inspectors");
      setInspectors(data?.inspectors || []);
    } catch (error) {
      console.error("Fehler beim Laden der Prüfer:", error);
    }
    setIsLoading(false);
  };

  const saveInspector = async () => {
    if (!newInspector.name.trim()) {
      toast.error("Bitte geben Sie einen Namen ein");
      return;
    }

    setIsSaving(true);
    try {
      await requestApiJson("/inspectors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newInspector),
      });

      toast.success("Prüfer hinzugefügt");
      setNewInspector({ name: "", email: "", phone: "" });
      loadInspectors();
    } catch (error) {
      toast.error(getFriendlyApiMessage(error));
    }
    setIsSaving(false);
  };

  const deleteInspector = async (inspectorId) => {
    try {
      await requestApiJson(`/inspectors/${inspectorId}`, {
        method: "DELETE",
      });

      toast.success("Prüfer gelöscht");
      loadInspectors();
    } catch (error) {
      toast.error(getFriendlyApiMessage(error));
    }
  };

  const selectInspector = (inspector) => {
    onSelectInspector(inspector.name);
    toast.success(`Prüfer "${inspector.name}" ausgewählt`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="inspector-manager-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Prüfer verwalten
          </DialogTitle>
        </DialogHeader>

        {/* Add new inspector */}
        <div className="p-4 bg-slate-50 rounded-lg border">
          <Label className="text-sm font-medium mb-2 block">Neuen Prüfer hinzufügen</Label>
          <div className="space-y-2">
            <Input
              value={newInspector.name}
              onChange={(e) => setNewInspector({ ...newInspector, name: e.target.value })}
              placeholder="Name *"
              data-testid="inspector-name-input"
            />
            <div className="flex gap-2">
              <Input
                value={newInspector.email}
                onChange={(e) => setNewInspector({ ...newInspector, email: e.target.value })}
                placeholder="E-Mail (optional)"
                type="email"
                data-testid="inspector-email-input"
              />
              <Input
                value={newInspector.phone}
                onChange={(e) => setNewInspector({ ...newInspector, phone: e.target.value })}
                placeholder="Telefon (optional)"
                data-testid="inspector-phone-input"
              />
            </div>
            <Button onClick={saveInspector} disabled={isSaving} className="w-full" data-testid="save-inspector-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Prüfer hinzufügen
            </Button>
          </div>
        </div>

        {/* Inspectors list */}
        <div className="mt-4">
          <Label className="text-sm font-medium mb-2 block">
            Gespeicherte Prüfer {currentInspector && <span className="text-muted-foreground">(Aktuell: {currentInspector})</span>}
          </Label>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : inspectors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Keine Prüfer vorhanden
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {inspectors.map((inspector) => (
                <div
                  key={inspector.id}
                  className={`flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-300 transition-colors ${
                    currentInspector === inspector.name ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                  data-testid={`inspector-${inspector.id}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{inspector.name}</div>
                    <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                      {inspector.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {inspector.email}
                        </span>
                      )}
                      {inspector.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {inspector.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => selectInspector(inspector)}
                      data-testid={`select-inspector-${inspector.id}`}
                    >
                      Auswählen
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteInspector(inspector.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-inspector-${inspector.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InspectorManager;
